// AppointmentsTable.jsx
import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; // Import the component
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons'; // Import specific icons
import { useAppointment } from '../contexts/AppointmentContext';
import { useNavigateWithContext } from '../hooks/useNavigateWithContext';
import GenericConfirmationModal from './GenericConfirmationModal';
import DataTable from 'react-data-table-component';
import Spinner from './Spinner';
import { isValid as isValidDate } from 'date-fns';
import { toast } from 'react-hot-toast';
import { getAppointmentActionState, isActionableAppointmentStatus, refundFailureMessage } from '../utilities/appointmentRules';
import { useMinuteTicker } from '../hooks/useMinuteTicker';

const AppointmentsTable = () => {
  const { appointments, isLoading, error, refreshAppointments, cancelAppointment} = useAppointment();
  const [FilteredAppts, setFilteredAppts] = useState([]);
  const [mobileVisibleCount, setMobileVisibleCount] = useState(10);
  const  navigateWithContext  = useNavigateWithContext();
  const [ showCancelConfirm, setShowCancelConfirm ] = useState({show: false, apptId: null});
  const [isCanceling, setIsCanceling] = useState(false);
  useMinuteTicker();

  // set the filtered rows to the full set when it changes
  useEffect(() => {
    if (appointments) {
      setFilteredAppts(appointments);
    }
  }, [appointments]);

  useEffect(() => {
    setMobileVisibleCount(10);
  }, [FilteredAppts.length]);

  const formatAppointmentDate = (dateValue) => {
    const dt = new Date(dateValue);
    if (!isValidDate(dt)) {
      return { date: 'Not available', time: 'Not available' };
    }

    return {
      date: dt.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      }),
      time: dt.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }),
    };
  };

  const renderAppointmentAction = (row, layout = 'table') => {
    if (!isActionableAppointmentStatus(row.status)) {
      return null;
    }

    const { isJoinable, isCancellable, isMissed } = getAppointmentActionState(row);
    const baseClass = layout === 'card'
      ? 'flex-1 px-3 py-2 text-sm rounded text-center whitespace-nowrap'
      : 'w-28 h-8 px-3 py-1 text-xs rounded whitespace-nowrap';

    if (isJoinable) {
      return (
        <button
          onClick={() => navigateWithContext(row.appointment_id, "/encounter", "appointment")}
          className={`${baseClass} bg-green-600 text-white hover:bg-green-700`}
          title={`Join encounter starting at ${new Date(row.date_appt).toLocaleTimeString()}`}
        >
          Join Now
        </button>
      );
    }

    if (isCancellable) {
      return (
        <button
          onClick={() => handleCancelAppt(row.appointment_id)}
          className={`${baseClass} bg-red-600 text-white hover:bg-red-700`}
          title="Cancel this appointment"
        >
          Cancel
        </button>
      );
    }

    if (isMissed) {
      return <div className="text-xs text-gray-500">Missed</div>;
    }

    return null;
  };

  const renderMobileAppointmentCard = (row) => {
    const formatted = formatAppointmentDate(row.date_appt);
    const action = renderAppointmentAction(row, 'card');

    return (
      <article key={row.appointment_id || `${row.date_appt}-${row.complaint}`} className="border border-gray-200 rounded bg-white p-4 shadow-sm space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h5 className="font-semibold text-primary">{formatted.date}</h5>
            <p className="text-sm text-neutral-dark">{formatted.time}</p>
          </div>
          <span className="text-xs px-2 py-1 rounded bg-neutral-light text-neutral-dark whitespace-nowrap">
            {row.status || 'Status unknown'}
          </span>
        </div>

        <div className="space-y-2 text-sm text-neutral-dark">
          <p>
            <span className="block text-xs uppercase text-gray-500">Reason</span>
            {row.complaint || 'Not provided'}
          </p>
          {row.completion_notes && (
            <p>
              <span className="block text-xs uppercase text-gray-500">Notes</span>
              {row.completion_notes}
            </p>
          )}
          {row.appointment_id && (
            <p>
              <span className="block text-xs uppercase text-gray-500">Appointment ID</span>
              {row.appointment_id}
            </p>
          )}
        </div>

        {action && <div className="flex flex-wrap gap-2 pt-1">{action}</div>}
      </article>
    );
  };

  const columns = [
    {
      name: 'Appt Date',
      selector: row => row.date_appt,
      sortable: true,
     cell: row => {
        const formatted = formatAppointmentDate(row.date_appt);
        return (
          <div className="flex flex-col">
            <span className="text-sm text-center">
              {formatted.date}
            </span>
            <span className="text-sm text-gray-600 text-center">
              {formatted.time}
            </span>
          </div>
        );
      },
      width: '8em',
    },
    {
      name: 'Reason',
      selector: row => row.complaint,
      sortable: true,
      wrap: true,
      cell: row => (
        <div className="md:w-20em">{row.complaint}</div> // Adjust width on medium screens
      ),
    },
    {
      name: 'Notes',
      selector: row => row.completion_notes,
      sortable: true,
      wrap: true,
      cell: row => (
        <div className="hidden md:table-cell">{row.completion_notes}</div>
      ),
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      width: '8em',
      cell: row => (
        <div className="hidden md:table-cell">{row.status}</div>
      ),
    },
    {
      name: 'Action',
      // sortable: false, // Actions usually aren't sortable based on button logic
      cell: row => {
        const action = renderAppointmentAction(row);
        return <div className="flex w-full justify-center">{action}</div>;
      },
      width: '10em', // Adjust width as needed for buttons
      ignoreRowClick: true, // Prevent row click from triggering if action button exists
    },
  ];

  const customStyles = {
    table: {
      style: {
        display: 'inline-block', // Collapse table to fit content on small screens
        width: 'auto', // Allow table to shrink to content width
        '@media (min-width: 768px)': {
          display: 'block', // Full width on medium screens and up
          width: '100%',
        },
      },
    },
    headRow: {
      style: {
        className:"bg-secondary-light text-primary-dark",
        display: 'flex',
        flexWrap: 'nowrap',
        width: '100%',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      },
    },
    headCells: {
      style: {
        fontSize: '1em',
        fontWeight: '600',
        textAlign: 'left',
        padding: '8px',
        flex: 'none',
        boxSizing: 'border-box',
        // Hide headers for Directions (4th), Qty (5th), Status (6th), Refill Dt (7th), and Refill button (8th) on small screens
        '&:nth-child(4), &:nth-child(5), &:nth-child(6), &:nth-child(7), &:nth-child(8)': {
          display: 'none',
        },
        '@media (min-width: 768px)': {
          '&:nth-child(4), &:nth-child(5), &:nth-child(6), &:nth-child(7), &:nth-child(8)': {
            display: 'table-cell',
          },
        },
      },
    },
    rows: {
      style: {
        display: 'flex',
        flexWrap: 'nowrap',
        width: '100%',
      },
    },
    cells: {
      style: {
        borderTop: '1px solid #e5e7eb',
        fontSize: '.75rem',
        fontWeight: '400',
        textAlign: 'left',
        padding: '8px',
        flex: 'none',
        boxSizing: 'border-box',
      },
    },
  };
  
  const handleRefresh = async () => {
    refreshAppointments();
  };
  
  const handleCancelAppt = (apptId) => {
    setShowCancelConfirm({
      show: true,
      apptId: apptId,
    });
  };

  // --- Event Handlers ---
  const handleSearch = (e) => {
    const search = e.target.value.toLowerCase();
    if (!Array.isArray(appointments)) {
      setFilteredAppts([]);
      return;
    }

    const searchableColumns = columns.filter(
      col => typeof col.selector === 'function'
    );

    const filtered = appointments.filter(row =>
      searchableColumns.some(col => {
        try {
          const raw = col.selector(row);
          if (raw == null) return false;

          // --- If it's a real Date object, compare against both date formats ---
          if (raw instanceof Date && isValidDate(raw)) {
            const iso       = raw.toISOString().slice(0, 10);        // "YYYY-MM-DD"
            const locale    = raw.toLocaleDateString();              // user locale, e.g. "10/31/2024"
            const lcIso     = iso.toLowerCase();                     // already lower, but safe
            const lcLocale  = locale.toLowerCase();
            return (
              lcIso.includes(search)    ||  // e.g. typing "2024-10" or "2024"
              lcLocale.includes(search)     // e.g. typing "10/31" or "oct 31"
            );
          }

          // --- Otherwise just string-match the raw value ---
          const str = String(raw).toLowerCase();
          return str.includes(search);

        } catch {
          return false;
        }
      })
    );

    setFilteredAppts(filtered);
  };
  
  return (
    <div className="mb-4 flex flex-col h-full">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl text-primary">All Appointments</h2>
        <button 
          className="text-xl text-gray-400 hover:text-primary-light"
          onClick={handleRefresh}
          title="Refresh"
        >
          <FontAwesomeIcon icon={faArrowsRotate} />
        </button>
      </div>

      {/* Loading State */}
      {isLoading && <Spinner  size="md" overlay={true} />}

      {/* Error State */}
      {!isLoading && error && <p className="text-red-500">{error}</p>}

      {/* Data Table Section (only render if not loading and no error) */}
      {!isLoading && !error && (
        <>
          {/* Search and Info Bar */}
          <div className="flex mb-2 w-full h-auto bg-gray-50 p-2 rounded border border-gray-200 items-center">
            <input
              type="text"
              placeholder="Search appointments...  use YYYY-MM-DD or m/d/y for dates"
              className="flex-grow p-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-light text-sm"
              onChange={handleSearch}
            />
            <div className="ml-4 text-sm text-gray-600 whitespace-nowrap">
              Showing {FilteredAppts.length} of {appointments.length}
            </div>
          </div>

            <div className="md:hidden space-y-3">
              {appointments.length > 0 ? (
                FilteredAppts?.length > 0 ? (
                  <>
                    {FilteredAppts.slice(0, mobileVisibleCount).map(renderMobileAppointmentCard)}
                    {FilteredAppts.length > mobileVisibleCount && (
                      <button
                        type="button"
                        onClick={() => setMobileVisibleCount(count => count + 10)}
                        className="w-full px-4 py-2 border border-primary text-primary rounded hover:bg-primary hover:text-white transition duration-200"
                      >
                        Show 10 more
                      </button>
                    )}
                  </>
                ) : (
                  <p className="text-center p-4">No matching appointments found.</p>
                )
              ) : (
                <p className="text-center p-4">No appointments found.</p>
              )}
            </div>

            {/* Table Container */}
            <div className="hidden md:block overflow-x-auto"> {/* Added overflow-x-auto for horizontal scrolling if needed */}
              {appointments.length > 0 ? (
                FilteredAppts?.length > 0 ? (
                  <DataTable
                    columns={columns}
                    data={FilteredAppts}
                    pagination={true}
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]} // Added more options
                    highlightOnHover
                    sortable
                    customStyles={customStyles}
                    responsive // Added for better mobile handling by DataTable
                    striped // Optional: Add zebra striping
                  />
                ) : (
                  <p className="text-center p-4">No matching appointments found.</p>
                )
              ) : (
              <p className="text-center p-4">No appointments found.</p>
            )}
          </div>
        </>
      )}
      {showCancelConfirm?.show && (
        <GenericConfirmationModal
          isOpen={showCancelConfirm.show}
          onCancel={() => setShowCancelConfirm({})}
          onConfirm={async () => {
            setIsCanceling(true);
            const result = await cancelAppointment(showCancelConfirm.apptId);
            setIsCanceling(false);
            if (result?.error) {
              toast.error(result.error);
              return;
            }
            setShowCancelConfirm({});
            toast.success('Appointment canceled.');
            const refundMsg = refundFailureMessage(result);
            if (refundMsg) toast.error(refundMsg, { duration: 10000 });
          }}
          title="Cancel Appointment"
          message="Are you sure you want to cancel this appointment?"
          textCancel="No"
          textConfirm={isCanceling ? 'Canceling...' : 'Yes'}
        />
      )}

    </div>
  );
};

export default AppointmentsTable;
